/**
 * The Markdown renderer of the note (`react-markdown` + `remark-gfm`, with the unified pipeline
 * behind them) is imported only through this dynamic `import()`, so the bundler emits it as a
 * separate chunk that the first screen never loads, like the calendar module
 * (`features/importantDates/loadCalendar.ts`). Until it arrives, or when loading fails, the note
 * shows as plain text. Types may be imported statically (`import type`); a static value import
 * of `./NoteMarkdown` anywhere in first-screen code would pull the chunk back in.
 */
export type NoteMarkdownModule = typeof import('./NoteMarkdown');

let pending: Promise<NoteMarkdownModule> | null = null;
let loaded: NoteMarkdownModule | null = null;

/** The module if it was loaded already (a card mounted later renders Markdown at once). */
export const loadedNoteMarkdown = (): NoteMarkdownModule | null => loaded;

/** Load the module once; a failed load is forgotten so that the next call tries again. */
export function loadNoteMarkdown(): Promise<NoteMarkdownModule> {
  pending ??= import('./NoteMarkdown').then(
    (module) => {
      loaded = module;
      return module;
    },
    (error: unknown) => {
      pending = null;
      throw error;
    },
  );
  return pending;
}
